import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useConsent } from "@/hooks/useConsent";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const PrivacySettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { consent, updateConsent, acceptAll, rejectAll } = useConsent();

  // Local state initialised once from stored consent; page re-mounts on navigation
  const [analyticsEnabled, setAnalyticsEnabled] = useState(consent?.analytics ?? false);
  const [marketingEnabled, setMarketingEnabled] = useState(consent?.marketing ?? false);
  const [functionalEnabled, setFunctionalEnabled] = useState(consent?.functional ?? false);

  const handleSave = () => {
    updateConsent({
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
      functional: functionalEnabled,
    });
    toast.success("Privacy preferences saved");
    setTimeout(() => navigate("/"), 500);
  };

  const handleAcceptAll = () => {
    acceptAll();
    toast.success("All cookies accepted");
    setTimeout(() => navigate("/"), 500);
  };

  const handleRejectAll = () => {
    rejectAll();
    toast.success("Non-essential cookies rejected");
    setTimeout(() => navigate("/"), 500);
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Privacy Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage how we use cookies and tracking on this portal.
        </p>
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Essential</CardTitle>
                <CardDescription>Required for core portal functionality</CardDescription>
              </div>
              <Switch checked disabled aria-label="Essential cookies (always on)" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              These cookies are necessary for the portal to function. They cannot be disabled.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  <Label htmlFor="analytics-setting">Analytics</Label>
                </CardTitle>
                <CardDescription>Help us improve the portal experience</CardDescription>
              </div>
              <Switch
                id="analytics-setting"
                checked={analyticsEnabled}
                onCheckedChange={setAnalyticsEnabled}
                aria-label="Analytics cookies"
              />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              We use Google Analytics (GA4) to understand how consultants use the portal. No
              personally identifiable information is shared with Google.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  <Label htmlFor="marketing-setting">Marketing</Label>
                </CardTitle>
                <CardDescription>Personalised content and advertisements</CardDescription>
              </div>
              <Switch
                id="marketing-setting"
                checked={marketingEnabled}
                onCheckedChange={setMarketingEnabled}
                aria-label="Marketing cookies"
              />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Used to show relevant job opportunities and services based on your profile.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  <Label htmlFor="functional-setting">Functional</Label>
                </CardTitle>
                <CardDescription>Enhanced features and saved preferences</CardDescription>
              </div>
              <Switch
                id="functional-setting"
                checked={functionalEnabled}
                onCheckedChange={setFunctionalEnabled}
                aria-label="Functional cookies"
              />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              Enables advanced portal features such as language preferences and UI customisation.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={handleSave}>Save Preferences</Button>
        <Button variant="outline" onClick={handleAcceptAll}>
          Accept All
        </Button>
        <Button variant="outline" onClick={handleRejectAll}>
          Reject All
        </Button>
      </div>
    </div>
  );
};

export default PrivacySettingsPage;
